import { useMemo,useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { fighters } from './content'
import { CombatMemoryBuffer,combatMemoryView,type CombatMemoryReplay } from './combatMemory'
import { deriveRealityFrame } from './realityMath'
import { useFightStore } from './store'

const GHOSTS=12
export function CombatMemoryEngine(){
  const buffer=useMemo(()=>new CombatMemoryBuffer(8,200),[])
  const replay=useRef<CombatMemoryReplay|null>(null),replayAge=useRef(0),lastStatus=useRef(''),lastMatch=useRef(-1),recordClock=useRef(0)
  const playerGhosts=useRef<Array<THREE.Mesh|null>>(Array(GHOSTS).fill(null)),enemyGhosts=useRef<Array<THREE.Mesh|null>>(Array(GHOSTS).fill(null))
  useFrame((state,dt)=>{
    const s=useFightStore.getState()
    if(s.matchSerial!==lastMatch.current){lastMatch.current=s.matchSerial;buffer.clear();replay.current=null;replayAge.current=0;combatMemoryView.active=false}
    if(s.status==='fight'){
      recordClock.current+=dt
      if(recordClock.current>=.05){
        recordClock.current=0
        const reality=deriveRealityFrame(s.player,s.enemy,s.impactCue?.kind,s.impactCue?.attackKind)
        buffer.record({t:state.clock.elapsedTime,player:[s.player.x,s.player.y,s.player.z],enemy:[s.enemy.x,s.enemy.y,s.enemy.z],impactSerial:s.impactSerial,score:reality.cinematic*.52+reality.intensity*.28+(s.impactCue?.attackKind==='special'?.35:s.impactCue?.attackKind==='heavy'?.16:0),playerAttack:s.player.lastAttack,enemyAttack:s.enemy.lastAttack})
      }
    }
    const ended=s.status==='round-over'||s.status==='match-over'
    if(ended&&lastStatus.current!==s.status){replay.current=buffer.bestReplay(8);replayAge.current=0}
    lastStatus.current=s.status
    if(!ended||!replay.current){combatMemoryView.active=false;for(const mesh of [...playerGhosts.current,...enemyGhosts.current])if(mesh)mesh.visible=false;return}
    replayAge.current+=dt
    const duration=s.status==='match-over'?4.2:1.18,progress=Math.min(1,replayAge.current/duration),main=buffer.sample(replay.current,progress)
    if(!main){combatMemoryView.active=false;return}
    combatMemoryView.active=true;combatMemoryView.player=[...main.player];combatMemoryView.enemy=[...main.enemy];combatMemoryView.intensity=Math.min(1,main.score);combatMemoryView.progress=progress
    const pColor=fighters[s.playerIndex]?.accent??'#75e7ff',eColor=fighters[s.enemyIndex]?.accent??'#ff6f91'
    for(let i=0;i<GHOSTS;i++){
      const trailProgress=Math.max(0,progress-i*.035),sample=buffer.sample(replay.current,trailProgress),fade=(1-i/GHOSTS)*(.16+main.score*.24)
      const apply=(mesh:THREE.Mesh|null,pos:[number,number,number],color:string,side:number)=>{
        if(!mesh||!sample)return
        mesh.visible=true;mesh.position.set(pos[0],pos[1]+1.05,pos[2]+side*.04);mesh.scale.setScalar(.34+i*.012)
        const material=mesh.material as THREE.MeshBasicMaterial;material.color.set(color);material.opacity=fade*(1-progress*.22)
      }
      if(sample){apply(playerGhosts.current[i],sample.player,pColor,-1);apply(enemyGhosts.current[i],sample.enemy,eColor,1)}
    }
    if(progress>=1&&s.status!=='match-over')combatMemoryView.active=false
  })
  return <group renderOrder={25}>
   {Array.from({length:GHOSTS},(_,i)=><mesh key={'p'+i} ref={m=>{playerGhosts.current[i]=m}} visible={false}><sphereGeometry args={[.22,10,8]}/><meshBasicMaterial transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending}/></mesh>)}
   {Array.from({length:GHOSTS},(_,i)=><mesh key={'e'+i} ref={m=>{enemyGhosts.current[i]=m}} visible={false}><sphereGeometry args={[.22,10,8]}/><meshBasicMaterial transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending}/></mesh>)}
  </group>
}
